import React from 'react';

/**
 * The floor under a lazily-loaded route.
 *
 * Code splitting means a route can now fail to LOAD, not just to render, and
 * React unmounts the whole tree when a Suspense child throws with no boundary
 * above it. Measured: with a chunk 404ing, `lazyRoute` correctly retried and
 * reloaded once, then gave up as designed — and left a completely blank page,
 * 0 characters, no explanation and no way out. A single bundle could never do
 * that, so nothing here had needed a boundary before.
 *
 * A chunk that is missing after one reload is not transient: the deploy is
 * broken, or the CDN is serving a partial upload. So this does not retry
 * silently — it says what happened and offers the two things that can
 * actually help, reloading and going somewhere else.
 */
export class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Left as console rather than swallowed: this is the only trace of a
    // broken deploy that reaches anyone, and it is worth having in a session
    // recording or a bug report.
    console.error('Route failed to load:', error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    // A failed dynamic import reads very differently from a crash inside a
    // page that did load, and the remedy is different too.
    const isChunk = /Loading chunk|Failed to fetch dynamically imported|Importing a module script failed/i
      .test(this.state.error?.message || '');

    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-canvas px-6 text-center">
        <p className="text-h5 text-ink">
          {isChunk ? 'This part of the app didn’t load' : 'Something went wrong here'}
        </p>
        <p className="max-w-[46ch] text-body-sm text-ink-muted">
          {isChunk
            ? 'That usually means a new version was just deployed. Reloading should pick it up.'
            : 'The page hit an error it couldn’t recover from. Reloading usually clears it.'}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => {
              // Clear the one-reload guard so this deliberate attempt is not
              // suppressed by the automatic one that already ran.
              try { sessionStorage.removeItem('os:chunk-reload'); } catch (e) { /* private mode */ }
              window.location.reload();
            }}
            className="rounded-pill bg-accent px-5 py-2.5 text-body-sm font-medium text-accent-ink"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={() => {
              try { sessionStorage.removeItem('os:chunk-reload'); } catch (e) { /* private mode */ }
              window.location.href = '/dashboard';
            }}
            className="rounded-pill border border-line px-5 py-2.5 text-body-sm text-ink-body"
          >
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
